import { Bell, Download, Search, Star } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { formatNumber } from "../lib/format";
import type { UserSession } from "../lib/types";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

type AccountAvailability = "loading" | "ready" | "error";

const SEARCH_DEBOUNCE_MS = 200;

interface DashboardHeaderProps {
  query: string;
  onQueryChange: (query: string) => void;
  session: UserSession | null;
  favoriteCount: number;
  onSignIn: () => void;
  onSignOut?: () => void;
  accountAvailability: AccountAvailability;
  canExport: boolean;
  onExportResults: () => void;
  onOpenWorkspace?: () => void;
}

export function DashboardHeader({
  query,
  onQueryChange,
  session,
  favoriteCount,
  onSignIn,
  onSignOut,
  accountAvailability,
  canExport,
  onExportResults,
  onOpenWorkspace
}: DashboardHeaderProps) {
  // Keystrokes render instantly from local state; propagation to filter state is
  // debounced so the screener (and its LRU cache) is not hit on every keystroke.
  const [draftQuery, setDraftQuery] = useState(query);
  const lastPropagatedQueryRef = useRef(query);
  const debounceTimerRef = useRef<number | null>(null);
  const onQueryChangeRef = useRef(onQueryChange);
  onQueryChangeRef.current = onQueryChange;

  const clearDebounceTimer = () => {
    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  };

  // Programmatic/URL-driven query changes must win over a stale local draft.
  useEffect(() => {
    if (query === lastPropagatedQueryRef.current) return;
    lastPropagatedQueryRef.current = query;
    clearDebounceTimer();
    setDraftQuery(query);
  }, [query]);

  useEffect(() => clearDebounceTimer, []);

  const handleQueryInput = (nextQuery: string) => {
    setDraftQuery(nextQuery);
    clearDebounceTimer();
    debounceTimerRef.current = window.setTimeout(() => {
      debounceTimerRef.current = null;
      lastPropagatedQueryRef.current = nextQuery;
      onQueryChangeRef.current(nextQuery);
    }, SEARCH_DEBOUNCE_MS);
  };

  const accountLabel =
    session === null
      ? accountAvailability === "loading"
        ? "Checking account access"
        : "Account status unavailable"
      : session.authenticated && session.email
        ? `Signed in as ${session.email}`
        : "Sign in to save favorite tickers";
  const accountDetail =
    session === null
      ? "Session state has not been confirmed."
      : session.authenticated
        ? `${formatNumber(favoriteCount)} favorite ${favoriteCount === 1 ? "ticker" : "tickers"}`
        : "Email magic-link login unlocks favorites and alerts.";

  return (
    <header className="flex h-12 min-w-0 items-center gap-3 border-b border-border bg-surface px-3">
      <div className="hidden min-w-[152px] shrink-0 sm:block">
        <div className="text-sm font-semibold leading-4">TrackInsiders</div>
        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
          Market screener
        </div>
      </div>

      <div className="relative min-w-0 flex-1 sm:max-w-xl">
        <label htmlFor="canonical-command-search" className="sr-only">
          Command search
        </label>
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          id="canonical-command-search"
          className="h-8 pl-8 text-xs"
          placeholder="Ticker, company, insider, accession"
          value={draftQuery}
          onChange={(event) => handleQueryInput(event.target.value)}
        />
      </div>

      <div className="ml-auto flex min-w-0 items-center gap-2 text-right">
        {onOpenWorkspace ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            aria-label="Open workspace"
            className="h-8 px-2"
            onClick={onOpenWorkspace}
          >
            <Bell className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="hidden sm:inline">Workspace</span>
          </Button>
        ) : null}
        <Button
          type="button"
          size="sm"
          aria-label="Export results"
          className="h-8 px-2"
          disabled={!canExport}
          onClick={onExportResults}
        >
          <Download className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="hidden lg:inline">Export</span>
        </Button>
        <div className="hidden min-w-0 md:block">
          <div className="truncate text-xs font-medium">{accountLabel}</div>
          <div className="flex items-center justify-end gap-1 truncate text-[10px] text-muted-foreground">
            {session?.authenticated ? <Star className="h-3 w-3" aria-hidden="true" /> : null}
            {accountDetail}
          </div>
        </div>
        {session?.authenticated === false ? (
          <Button type="button" size="sm" variant="outline" className="h-8 px-2 text-xs" onClick={onSignIn}>
            Sign in
          </Button>
        ) : session?.authenticated ? (
          <Button type="button" size="sm" variant="ghost" className="h-8 px-2 text-xs" onClick={onSignOut}>
            Sign out
          </Button>
        ) : null}
      </div>
    </header>
  );
}
